import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Eye, Star, ExternalLink } from "lucide-react";
import { adminApi, mediaUrl } from "../api";
import { AField, Btn, Modal, PageHead, StatusPill, Toggle, Uploader, inputCls, fmtDate } from "../components";

const CATEGORIES = ["CULTURE", "MUSIC", "BUSINESS", "LIFESTYLE", "ENTERTAINMENT", "PEOPLE", "COMMUNITY", "COMMENTARY"];
const EMPTY = { title: "", author: "The Cobain Report", category: "CULTURE", excerpt: "", image: "", body: "", featured: false, status: "draft" };

export default function Articles() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.get("/admin/articles").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (a) => {
    setForm({
      title: a.title, author: a.author || "", category: a.category || "CULTURE",
      excerpt: a.excerpt || "", image: a.image || "",
      body: typeof a.body === "string" ? a.body : (a.body || []).join("\n\n"),
      featured: !!a.featured, status: a.status,
    });
    setEditing(a.id);
  };

  const save = async (status) => {
    setSaving(true);
    try {
      const payload = { ...form, status };
      if (editing === "new") await adminApi.post("/admin/articles", payload);
      else await adminApi.put(`/admin/articles/${editing}`, payload);
      setEditing(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (a, status) => {
    await adminApi.post(`/admin/articles/${a.id}/status`, { status });
    load();
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}" permanently?`)) return;
    await adminApi.delete(`/admin/articles/${a.id}`);
    load();
  };

  return (
    <div data-testid="admin-articles">
      <PageHead title="EDITORIAL" sub="Create, publish and manage the magazine.">
        <Btn onClick={() => { setForm(EMPTY); setEditing("new"); }} data-testid="articles-new">
          <Plus size={13} /> NEW ARTICLE
        </Btn>
      </PageHead>

      <div className="border border-woodborder" data-testid="articles-list">
        {items.length === 0 && <p className="p-8 font-mono text-xs text-clay text-center">NO ARTICLES YET — CREATE THE FIRST ONE.</p>}
        {items.map((a, i) => (
          <div key={a.id} className="flex items-center gap-4 px-4 md:px-6 py-4 border-b border-woodborder last:border-b-0 hover:bg-card/30 transition-colors" data-testid={`article-row-${i}`}>
            {a.image ? (
              <img src={mediaUrl(a.image)} alt="" className="w-14 h-14 object-cover border border-woodborder shrink-0 hidden sm:block" />
            ) : (
              <div className="w-14 h-14 border border-woodborder bg-wood hidden sm:flex items-center justify-center font-display text-clay text-xl">C</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-editorial font-bold text-cream text-sm truncate">{a.title}</p>
                {a.featured && <Star size={12} className="text-gold fill-gold shrink-0" />}
              </div>
              <p className="mt-1 font-mono text-[10px] tracking-[0.12em] text-clay">
                {a.category} — {fmtDate(a.published_at || a.created_at)} — {a.slug}
              </p>
            </div>
            <StatusPill status={a.status} />
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => setPreview(a)} className="p-2 text-clay hover:text-tan" title="Preview" data-testid={`article-preview-${i}`}><Eye size={16} /></button>
              <button onClick={() => openEdit(a)} className="p-2 text-clay hover:text-cream" title="Edit" data-testid={`article-edit-${i}`}><Pencil size={16} /></button>
              {a.status === "published" ? (
                <a href={`/read/${a.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-clay hover:text-gold-bright" title="View live" data-testid={`article-live-${i}`}><ExternalLink size={16} /></a>
              ) : null}
              <Btn variant={a.status === "published" ? "ghost" : "gold"} className="!px-3 !py-1.5 hidden md:inline-flex" onClick={() => setStatus(a, a.status === "published" ? "draft" : "published")} data-testid={`article-toggle-${i}`}>
                {a.status === "published" ? "UNPUBLISH" : "PUBLISH"}
              </Btn>
              <button onClick={() => remove(a)} className="p-2 text-clay hover:text-red-400" title="Delete" data-testid={`article-delete-${i}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "NEW ARTICLE" : "EDIT ARTICLE"} wide>
        <div className="space-y-5" data-testid="article-editor">
          <AField label="TITLE *"><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="editor-title" /></AField>
          <div className="grid sm:grid-cols-2 gap-5">
            <AField label="AUTHOR"><input className={inputCls} value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="editor-author" /></AField>
            <AField label="CATEGORY">
              <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="editor-category">
                {CATEGORIES.map((c) => <option key={c} value={c} className="bg-wood">{c}</option>)}
              </select>
            </AField>
          </div>
          <AField label="EXCERPT (OPTIONAL)"><input className={inputCls} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="editor-excerpt" /></AField>
          <AField label="FEATURED IMAGE">
            <div className="flex items-start gap-4 flex-wrap">
              <Uploader onUploaded={(p) => setForm({ ...form, image: p })} accept="image/*" label="UPLOAD IMAGE" testId="editor-image-upload" />
              {form.image && <img src={mediaUrl(form.image)} alt="Featured" className="w-24 h-24 object-cover border border-woodborder" data-testid="editor-image-preview" />}
            </div>
          </AField>
          <AField label="ARTICLE BODY (BLANK LINE BETWEEN PARAGRAPHS)">
            <textarea className={`${inputCls} min-h-[220px] resize-y`} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="editor-body" />
          </AField>
          <Toggle checked={form.featured} onChange={(v) => setForm({ ...form, featured: v })} label="FEATURED ARTICLE" testId="editor-featured" />
          <div className="flex flex-wrap gap-3 pt-2 border-t border-woodborder">
            <Btn variant="ghost" disabled={saving} onClick={() => save("draft")} data-testid="editor-save-draft">{saving ? "SAVING..." : "SAVE DRAFT"}</Btn>
            <Btn variant="gold" disabled={saving || form.title.trim().length < 2} onClick={() => save("published")} data-testid="editor-publish">{saving ? "SAVING..." : "PUBLISH"}</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={!!preview} onClose={() => setPreview(null)} title="PREVIEW" wide>
        {preview && (
          <div data-testid="article-preview-content">
            <span className="font-mono text-[10px] tracking-[0.2em] bg-burnt text-cream px-3 py-1">{preview.category}</span>
            <h2 className="mt-4 font-editorial font-extrabold text-cream text-3xl leading-tight">{preview.title}</h2>
            <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-tan">{(preview.author || "").toUpperCase()} — {fmtDate(preview.published_at || preview.created_at)}</p>
            {preview.image && <img src={mediaUrl(preview.image)} alt="" className="mt-5 w-full aspect-video object-cover border border-woodborder" />}
            <div className="mt-6 space-y-4">
              {(typeof preview.body === "string" ? preview.body.split(/\n\n+/) : preview.body || []).map((p, i) => (
                <p key={i} className="text-sand font-light leading-relaxed text-sm">{p}</p>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
